import type { Guide } from "./guide";

/** Guide 6: the one stream, resuming it, webhooks signed and verified, and the record of what was sent. */
export const EVENTS_AND_WEBHOOKS: Guide = {
  slug: "events-and-webhooks",
  samples: "events",
  title: "Events and webhooks",
  nav: "Events and webhooks",
  summary:
    "Follow a conversation as it happens, pick up after a drop, and have events sent to your server, signed.",
  minutes: 10,
  lede: "Every step Bash takes is an event. Follow one conversation as it happens, or have the lasting ones sent to your server, signed.",
  lines: ["events", "ask"],
  steps: [
    {
      id: "follow",
      title: "Follow a conversation",
      body: [
        "Its kept events come first, then each one as it happens ([`events`](/developers/events)).",
      ],
      doors: {
        ts: { sample: "guide-events-follow" },
        py: { sample: "guide-events-follow" },
        cli: { sample: "guide-events-follow" },
        http: { sample: "guide-events-follow" },
        mcp: { sample: "events" },
      },
      then: "Each event carries a `seq`, so you always know where you are.",
      prints: [
        "1 conversation.status replying (running)",
        "3 task.started Rebook the flight (task_91d)",
        "4 interaction.created approval int_3j9",
        "5 message.completed On it. I will ask before I pay.",
        "7 task.completed Rebooked on the 09:15.",
      ].join("\n"),
    },
    {
      id: "resume",
      title: "Pick up after a drop",
      body: ["Reconnect with the last `seq` you saw."],
      doors: {
        ts: { sample: "guide-events-resume" },
        py: { sample: "guide-events-resume" },
        cli: { sample: "guide-events-resume" },
        http: { sample: "guide-events-resume" },
        mcp: { sample: "guide-events-resume" },
      },
      then: "It starts at the next event: nothing is missed, and nothing comes twice.",
    },
    {
      id: "send",
      title: "Send them to your server",
      body: [
        "Webhooks get the events worth keeping: a task done, an approval asked. The reply as it is typed comes only on the stream.",
      ],
      doors: {
        ts: { sample: "guide-events-send" },
        py: { sample: "guide-events-send" },
        cli: { sample: "guide-events-send" },
        http: { sample: "guide-events-send" },
        mcp: {
          note: "Webhooks are set up once, from an SDK, the CLI or HTTP.",
        },
      },
      then: "Bash signs each one with your endpoint's secret, shown this once.",
      prints: [
        "Sending task.completed, interaction.created to https://api.tripnest.example/vestra as whk_2bq.",
        "Its signing secret, shown this once: whsec_…",
      ].join("\n"),
    },
    {
      id: "verify",
      title: "Check each one",
      body: [
        "The handler checks the signature with `VESTRA_WEBHOOK_SECRET`, then hands you the event. Give it the raw body: a parsed one fails the check.",
      ],
      doors: {
        ts: { sample: "guide-events-verify" },
        py: { sample: "guide-events-verify" },
        cli: {
          note: "Your server checks each webhook: with an SDK's handler, or by hand, as the HTTP tab says.",
        },
        http: {
          note: "`Vestra-Signature: t=<unix seconds>,v1=<hex>` is an HMAC-SHA256 of `<t>.<raw body>` with your secret. Refuse it when it does not match, or when `t` is over 300 seconds old.",
        },
        mcp: {
          note: "Webhooks reach your server, not an agent: check them with an SDK's handler.",
        },
      },
      then: "A signature it cannot trust, or one over 300 seconds old, is answered 400, and a good one 200. Anything but a 2xx is sent again later.",
    },
    {
      id: "missed",
      title: "Read what you missed",
      body: ["The record of what your webhooks were sent, newest first."],
      doors: {
        ts: { sample: "guide-events-missed" },
        py: { sample: "guide-events-missed" },
        cli: { sample: "guide-events-missed" },
        http: { sample: "guide-events-missed" },
        mcp: {
          note: "The record is read from an SDK, the CLI or HTTP. An agent follows a conversation with the `events` tool.",
        },
      },
      then: "`after` an event's id reads forward from it, so a server back from a restart catches up.",
      prints: [
        "id       type         user          conversation_id  task_id   created_at",
        "evt_4hq  task.failed  traveller-42  conv_8f2k        task_91d  2026-10-02T09:14:03.000Z",
      ].join("\n"),
    },
    {
      id: "whole",
      title: "The whole thing",
      body: ["Follow, send, check and catch up, in one program."],
      doors: {
        ts: { sample: "guide-events-whole" },
        py: { sample: "guide-events-whole" },
        cli: { sample: "guide-events-whole" },
        http: { sample: "guide-events-whole" },
        mcp: {
          note: "An agent follows a conversation with the `events` tool. Webhooks are your server's.",
        },
      },
      then: "Live while it runs, signed at your server, and a record for anything missed.",
    },
  ],
};
